import React, { FC, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Button, Box, Typography, Alert } from '@mui/material';
import CustomInput from '../../CustomInput';
import { useTranslation } from 'react-i18next';
import { useEmailMutation } from './mutation';
import { EmailDataModel } from './types';
import BaseModal from '../BaseModal/BaseModal';

type Props = {
  centered?: boolean;
  show: boolean;
  onHide: () => void;
  ticket: EmailDataModel['ticket'];
  handleSuccess: () => void;
};

const EmailModal: FC<Props> = ({ show, onHide, ticket, handleSuccess }) => {
  const { t } = useTranslation();
  const [isAlertShow, setIsAlertShow] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string | undefined>('');
  const { mutate, isLoading } = useEmailMutation();

  useEffect(() => {
    if (isAlertShow) {
      setTimeout(() => {
        setIsError(false);
        setIsAlertShow(false);
      }, 3000);
    }
  }, [isAlertShow]);

  const validate = (values: EmailDataModel) => {
    const errors: Partial<EmailDataModel> = {};
    
    if (!values.user_email) {
      errors.user_email = '*Ungültige E-Mail-Adresse';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.user_email)) {
      errors.user_email = '*Ungültige E-Mail-Adresse';
    }
    
    if (!values.subject) {
      errors.subject = '*Betreff eingeben';
    }
    
    if (!values.message) {
      errors.message = '*Kündigungsgrund angeben';
    }
    
    return errors;
  };

  const handleSubmit = (values: EmailDataModel) => {
    sendEmail(values);
  };

  const sendEmail = (data: EmailDataModel) => {
    mutate(data, {
      onSuccess() {
        handleSuccess();
        setIsAlertShow(true);
      },
      onError() {
        setIsError(true);
      },
      onSettled(data, error) {
        setStatusMessage((data as Partial<EmailDataModel>).message);
        console.error(error);
      },
    });
  };

  const handleHide = () => {
    setStatusMessage('');
    onHide();
  };

  return (
    <>
      {isAlertShow && (
        <Alert 
          severity={isError ? 'error' : 'success'}
          sx={{ maxWidth: '300px', mb: 2 }}
        >
          {isError ? 'Wird gesendet ...' : 'E-Mail senden'}
        </Alert>
      )}
      <BaseModal
        open={show}
        onClose={handleHide}
        title={t('emailModal.title')}
        actions={null}
      >
        <Formik
          initialValues={{ user_email: '', subject: '', message: '', ticket }}
          validate={validate}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" className="subtitle-muted" sx={{ mb: 2, textAlign: 'center', fontWeight: 'normal' }}>
                  {t('emailModal.subTitle')}
                </Typography>
                
                <CustomInput
                  required
                  name="user_email"
                  type="email"
                  label={t('emailModal.yourEmail')}
                  value={values.user_email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.user_email && Boolean(errors.user_email)}
                  helperText={touched.user_email && errors.user_email ? errors.user_email : ''}
                />
                
                <CustomInput
                  required
                  name="subject"
                  type="text"
                  label="Betreff"
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.subject && Boolean(errors.subject)}
                  helperText={touched.subject && errors.subject ? errors.subject : ''}
                />
                
                <CustomInput
                  required
                  name="message"
                  multiline
                  rows={7}
                  label={t('emailModal.message')}
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.message && Boolean(errors.message)}
                  helperText={touched.message && errors.message ? errors.message : ''}
                />
                
                {statusMessage && (
                  <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                    E-Mail erfolgreich gesendet.
                  </Typography>
                )}
                
                <Box display="flex" justifyContent="center" width="100%">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isSubmitting || isLoading}
                    className="danger-button"
                    sx={{ m: 2, mt: 0 }}
                  >
                    {isLoading ? t('emailModal.sending') : 'ABONNEMENT KÜNDIGEN'}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </BaseModal>
    </>
  );
};

export default EmailModal;
